import {
	type CaptureFileJSONObject,
	type CollectionFieldVectorFieldValue,
	type CollectionSummaryJSONObject,
	IkeFieldType,
	type LocationData,
	ParseJSON,
	SystemTag,
} from '@ikegps/core-api';
import type { CollectionModel } from '../collection.data';
import { IkeGeodesic } from '../../../geo/geodesic';
import { MultiPoint } from '../../../math';
import { IkeAngle } from '../../../unit';
import type { FormModelField } from '../../form/form.field';
import { CollectionModelField, ModelMutator } from '../collection.field';
import { registerFieldType } from './field.registry';
import type { CaptureProvider } from './provider/capture.provider';
import type { CollectionProvider } from './provider/collection.provider';

export type ModelFieldStateVector =
	| ModelFieldStateVectorManual
	| ModelFieldStateVectorP2P
	| ModelFieldStateVectorCollection;

export interface ModelFieldStateVectorManual {
	provider: 'manual';
	value: CollectionFieldVectorFieldValue;
}

export interface ModelFieldStateVectorP2P {
	provider: 'pointtopoint';
	/** List of captures used for P2P (Generally two) */
	captures: string[];
}

export interface ModelFieldStateVectorCollection {
	provider: 'collection';
	collectionId: string;
	fallbackValue?: CollectionFieldVectorFieldValue;
}

export class ModelMutatorVector extends ModelMutator<FormModelField, CollectionModelFieldVector> {
	manual(newValue: CollectionFieldVectorFieldValue | null) {
		if (newValue == null || (newValue.distance == null && newValue.bearing == null)) {
			this.state.setFieldValue<ModelFieldStateVector>(this.field.id, null);
			return;
		}

		// Force the vector to be between 0 and two Pi
		newValue.bearing = IkeAngle.normalize(newValue.bearing);

		// Sometimes pitch is not set, It is a required field
		if (newValue.pitch == null || isNaN(newValue.pitch)) {
			newValue.pitch = 0;
		}

		this.state.setFieldValue<ModelFieldStateVectorManual>(this.field.id, {
			provider: CollectionModelFieldVector.PROVIDER_MANUAL,
			value: newValue,
		});
	}

	p2p(state: string[]) {
		if (!Array.isArray(state)) {
			throw new Error(`Field ${this.field.id}: Invalid capture state.`);
		}
		if (state.length < 2) {
			throw new Error(`Field ${this.field.id}: Invalid capture count "${state.length}".`);
		}

		this.state.setFieldValue<ModelFieldStateVectorP2P>(this.field.id, {
			provider: CollectionModelFieldVector.PROVIDER_P2P,
			captures: state.slice(),
		});
	}

	collection(collectionId: string, fallbackValue?: CollectionFieldVectorFieldValue) {
		if (collectionId == null) {
			this.state.setFieldValue<ModelFieldStateVector>(this.field.id, null);
			return;
		}

		this.state.setFieldValue<ModelFieldStateVectorCollection>(this.field.id, {
			provider: CollectionModelFieldVector.PROVIDER_COLLECTION,
			collectionId,
			fallbackValue,
		});
	}

	reverse() {
		if (!this.field.hasValue()) {
			return;
		}

		if (this.field.isPointToPoint()) {
			const captures = this.field.stateValue.captures.slice().reverse();

			this.p2p(captures);
			return;
		}

		if (this.field.isManual()) {
			const value = this.field.value;
			this.manual({
				distance: value.distance,
				bearing: IkeAngle.flip(value.bearing),
				pitch: value.pitch,
			});
		}
	}

	override copy(from: CollectionModelFieldVector): boolean {
		if (from.isPointToPoint()) {
			this.p2p(from.stateValue.captures);
			return true;
		}

		if (from.isCollectionLink()) {
			const [targetCollection] = from.getCollections();
			// Dont link to ourself
			if (targetCollection == null) {
				return true;
			}

			if (targetCollection.collectionId === this.field.collection.id) {
				const otherCollection = from.collection;
				this.collection(otherCollection.id);
				return true;
			}

			this.collection(targetCollection.collectionId);
			return true;
		}

		if (from.isManual()) {
			this.manual(from.value);
			return true;
		}

		return false;
	}
}

export class CollectionModelFieldVector
	extends CollectionModelField<FormModelField, ModelFieldStateVector, CollectionFieldVectorFieldValue>
	implements CaptureProvider, CollectionProvider
{
	static readonly PROVIDER_MANUAL = 'manual' as const;
	static readonly PROVIDER_P2P = 'pointtopoint' as const;
	static readonly PROVIDER_COLLECTION = 'collection' as const;

	static TYPE: IkeFieldType = IkeFieldType.VECTOR;

	static isVectorField(f: CollectionModelField): f is CollectionModelFieldVector {
		return f.schema.type === CollectionModelFieldVector.TYPE;
	}

	private _Mutator: ModelMutatorVector;
	get Mutator() {
		if (this._Mutator == null) {
			this._Mutator = new ModelMutatorVector(this);
		}
		return this._Mutator;
	}

	override get value(): CollectionFieldVectorFieldValue {
		const stateValue = this.stateValue;
		if (stateValue == null) {
			return null;
		}

		if (stateValue.provider === CollectionModelFieldVector.PROVIDER_MANUAL) {
			return stateValue.value;
		}

		if (stateValue.provider === CollectionModelFieldVector.PROVIDER_P2P) {
			const vectorValue = MultiPoint.Points(this.getCaptures());
			// To be backwards compatible, vertical values should not have a sign on them
			vectorValue.vertical = Math.abs(vectorValue.vertical);
			return vectorValue;
		}

		if (stateValue.provider === CollectionModelFieldVector.PROVIDER_COLLECTION) {
			const collection = this.collection;
			const sourceLocation = collection.location;
			if (sourceLocation == null) {
				return stateValue.fallbackValue ?? null;
			}

			const targetCollection = collection.state.getCollection(stateValue.collectionId);
			if (targetCollection == null || targetCollection.tags.includes(SystemTag.deleted)) {
				return stateValue.fallbackValue ?? null;
			}

			let targetLocation: LocationData;

			const targetCollectionModel = collection.state.getCollectionModel(stateValue.collectionId);
			if (targetCollectionModel == null) {
				targetLocation = ParseJSON.fromGeoPoint(targetCollection.location);
			} else {
				targetLocation = targetCollectionModel.location;
			}

			if (targetLocation == null) {
				return stateValue.fallbackValue ?? null;
			}

			return IkeGeodesic.getVector(sourceLocation, targetLocation);
		}

		throw new Error(`Field ${this.id}: Invalid Provider type "${stateValue['provider']}"`);
	}

	get provider(): 'manual' | 'pointtopoint' | 'collection' {
		const stateValue = this.stateValue;
		if (stateValue == null || stateValue.provider == null) {
			return CollectionModelFieldVector.PROVIDER_MANUAL;
		}
		return stateValue.provider;
	}

	isManual(): this is CollectionModelField<
		FormModelField,
		ModelFieldStateVectorManual,
		CollectionFieldVectorFieldValue
	> {
		return this.provider === CollectionModelFieldVector.PROVIDER_MANUAL;
	}

	isPointToPoint(): this is CollectionModelField<
		FormModelField,
		ModelFieldStateVectorP2P,
		CollectionFieldVectorFieldValue
	> {
		return this.provider === CollectionModelFieldVector.PROVIDER_P2P;
	}

	isCollectionLink(): this is CollectionModelField<
		FormModelField,
		ModelFieldStateVectorCollection,
		CollectionFieldVectorFieldValue
	> {
		return this.provider === CollectionModelFieldVector.PROVIDER_COLLECTION;
	}

	getOwnedCaptures(): CaptureFileJSONObject[] {
		return this.getCaptures();
	}

	getCaptures(): CaptureFileJSONObject[] {
		const captures: CaptureFileJSONObject[] = [];
		const captureIds = this.getCaptureIds();
		for (const captureId of captureIds) {
			const capture = this.collection.state.getCapture(captureId);
			if (capture == null) {
				return [];
			}

			if (capture.orientation == null) {
				throw new Error(`Invalid truesize capture '${capture.objectId}' for field '${this.id}' (missing orientation)`);
			}

			if (capture.calibrationid == null) {
				throw new Error(
					`Invalid truesize capture '${capture.objectId}' for field '${this.id}' (missing calibrationId)`
				);
			}

			captures.push(capture);
		}
		return captures;
	}

	getOwnedCaptureIds(): string[] {
		return this.getCaptureIds();
	}
	getCaptureIds(): string[] {
		if (!this.isPointToPoint()) {
			return [];
		}
		return this.stateValue.captures;
	}

	getTargetModel(): CollectionModel | null {
		if (!this.isCollectionLink()) {
			return null;
		}
		return this.collection.state.getCollectionModel(this.stateValue.collectionId);
	}

	async getTargetModelAsync(): Promise<CollectionModel | null> {
		if (!this.isCollectionLink()) {
			return null;
		}
		return this.collection.state.getCollectionModelAsync(this.stateValue.collectionId);
	}

	getTargetJson(): CollectionSummaryJSONObject {
		if (!this.isCollectionLink()) {
			return null;
		}
		return this.collection.state.getCollection(this.stateValue.collectionId);
	}

	/**
	 * @deprecated Use getTargetJson or getTargetModel instead
	 */
	getCollections(): CollectionSummaryJSONObject[] {
		// There is no reason for this to be an array result when you can only link to one collection
		return this.getCollectionIds().map((c) => this.collection.state.getCollection(c));
	}

	/**
	 * @deprecated Use getTargetJson or getTargetModel instead
	 */
	getCollectionIds(): string[] {
		// There is no reason for this to be an array result when you can only link to one collection
		if (!this.isCollectionLink()) {
			return [];
		}
		return [this.stateValue.collectionId];
	}

	hasValue(): boolean {
		return this.value != null;
	}

	/**
	 * An async method which is guaranteed to load the linked CollectionModel.
	 * Can be used to force the linked collection to load before using `value` or `hasValue`.
	 */
	async getLinkedCollection(): Promise<CollectionModel> {
		if (this.stateValue?.provider !== CollectionModelFieldVector.PROVIDER_COLLECTION) {
			return null;
		}
		return await this.collection.state.getCollectionModelAsync(this.stateValue.collectionId);
	}

	isPartOfSpanSubForm() {
		// If the field's parent's parent is null, the field is at the top level, not in a subform
		if (this.parent.parent == null) {
			return false;
		}

		const spanTypeState = this.parent.schema.subFormSpanTypeState;

		// Check if the subform is a SPAN type
		if (!spanTypeState) {
			return false;
		}

		return spanTypeState.vectorFieldId === this.schema.id;
	}
}

registerFieldType(IkeFieldType.VECTOR, (parent, schema) => new CollectionModelFieldVector(parent, schema));
